import { container, ListingProduct, RdssCartService, RdssSimpleCart } from '@rdss/client'
import { i18n } from '@rdss/core'
import { createStore } from '@stencil/store'
import { addToCartTrackingData } from '~/utils/tracking'

/**
 * It's the Store.
 */
export const store = createStore({
    cartItemsCount: 0,
    cartIsLoading: false,
    translations: {},
    cart: {} as RdssSimpleCart,
})

/**
 * Translates a given key using the translation service and the application's current state.
 *
 * @param {string} key - The translation key to be translated.
 * @param {string} defaultValue - The default value to return if the translation is not found.
 * @param {Array} replaceValues - The default value to return if the translation is not found.
 * @returns {string} - The translated string or the default value if not found.
 *
 * @see translationService - The translation service function responsible for performing the translation.
 * @see store.state.translations - The translations stored in the application state.
 */
export const trans = (
    key: string,
    defaultValue?: string,
    replaceValues?: Array<string>
): string => {
    return i18n(key, defaultValue, replaceValues, store.state.translations)
}

/**
 * Adds a product to the cart.
 */
export const addToCart = async (detail: {
    quantity: number,
    orderNumber: string,
    context: string,
    useLoyaltyPoints: boolean
}, product?: ListingProduct): Promise<boolean> => {
    let requestSuccess = false;

    const cartService = container.resolve(RdssCartService)
    store.state.cartIsLoading = true
    if (detail.quantity > 0) {
        store.state.cart = (await cartService.getCart() as unknown as RdssSimpleCart)
        if (product) {
            addToCartTrackingData(product, detail)
        }
        await cartService
            .updateQuantity(detail.orderNumber, detail.quantity, detail.useLoyaltyPoints)
            .then((res) => {
                if (!res.ok) return
                store.state.cart = res as unknown as RdssSimpleCart
                requestSuccess = true;
            })
            .catch((e: any) => {
                console.error('Failed fetching cartService', e.message)
            })
            .finally(() => {
                store.state.cartIsLoading = false
            })
    } else {
        await cartService
            .deleteProduct(detail.orderNumber, detail.useLoyaltyPoints)
            .then((res) => {
                if (!res.ok) return
                store.state.cart = res as unknown as RdssSimpleCart
                requestSuccess = true;
            })
            .catch(e => console.error('Failed fetching cartService', e.message))
            .finally(() => {
                store.state.cartIsLoading = false
            })
    }
    store.state.cartItemsCount = store.state.cart.items.reduce((acc, item) => acc + item.quantity, 0)
    return requestSuccess;
}
